import { AlertTriangle } from 'lucide-react';

import { useInstitutionConfig } from '../../../../contexts/InstitutionConfigContext';

export interface APStatus {
  ap_scores_count?: number;
  courses_count?: number;
  total_credits?: number;
  plc_credits_capped?: number;
  [key: string]: unknown;
}

export interface APStatusSummaryProps {
  apStatus: APStatus | null | undefined;
}

const APStatusSummary = ({ apStatus }: APStatusSummaryProps) => {
  // Per-institution PLC cap (UMD 60, SJSU 30) — UX-R1 task 1.1. Hook must
  // run before the early return.
  const { config } = useInstitutionConfig();
  const plcCap = config.priorLearningCap;

  if (!apStatus) return null;

  const isPLCCapApplied = (apStatus.plc_credits_capped ?? 0) > 0;
  // "Approaching" at ≥83% of cap — same threshold as PLCCapIndicator. A null cap
  // (UC Merced — no total PLC cap, W41 M5) can never be approached.
  const isApproachingPLCCap =
    plcCap != null && (apStatus.total_credits ?? 0) >= plcCap * 0.83;

  return (
    <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-primary-900">AP Credits Summary</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <span className="text-gray-600">AP Scores:</span>
          <span className="ml-2 font-medium">{apStatus.ap_scores_count || 0}</span>
        </div>
        <div>
          <span className="text-gray-600">Courses:</span>
          <span className="ml-2 font-medium">{apStatus.courses_count || 0}</span>
        </div>
        <div>
          <span className="text-gray-600">Credits:</span>
          <span className="ml-2 font-medium">{apStatus.total_credits || 0}</span>
        </div>
        <div>
          <span className="text-gray-600">PLC Used:</span>
          <span className="ml-2 font-medium">{apStatus.total_credits || 0}/{plcCap}</span>
        </div>
      </div>

      {isPLCCapApplied && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-800">
              PLC Cap Applied: {apStatus.plc_credits_capped} credits reduced
            </span>
          </div>
          <p className="text-xs text-amber-700 mt-1">
            Maximum {plcCap} Prior Learning Credits allowed. Credits scaled proportionally.
          </p>
        </div>
      )}

      {isApproachingPLCCap && !isPLCCapApplied && (
        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-800">Approaching PLC Limit</span>
          </div>
          <p className="text-xs text-yellow-700 mt-1">
            You're near the {plcCap}-credit Prior Learning Credit maximum.
          </p>
        </div>
      )}
    </div>
  );
};

export default APStatusSummary;
